import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Checkbox, Modal, Radio, Spin, Typography, message } from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import api from '../../api/client';
import type { ServiceOrder } from '../../api/serviceOrders';
import { createPostingChain, runPostingCommand, waitPostingCommand } from './posting';
import type { ChainCommandInput, CommandOutcome } from './posting';
import { printSalesReceiptForm, printServiceOrderForm } from './printing';

const { Text } = Typography;

// Окно «Провести и оплатить» (OilChangePage.md №62/№63/№71/№72):
// цепочка команд posting_commands; печать документов — только через 1С.

type StepKey =
  | 'post_order' | 'print_service' | 'print_goods' | 'post_sale' | 'print_order'
  | 'post_service_sale' | 'create_service_invoice' | 'print_service_invoice'
  | 'create_goods_invoice' | 'print_goods_invoice';
type BusyKey = StepKey | 'sales_receipt' | 'all';

interface StepState {
  status: 'idle' | 'running' | 'done' | 'error' | 'timeout';
  text?: string;
}

interface Props {
  open: boolean;
  order: ServiceOrder;
  canPost: boolean;
  canFiscalize: boolean;
  canPrint: boolean;
  canInvoice: boolean;
  onClose: (changed: boolean) => void;
  // Перед цепочкой страница сохраняет актуальный черновик с выбранным
  // card_payment. Для проведённого/read-only заказа проп не передаётся —
  // тогда шапку чека выравнивает форма 1С по факту оплаты (v4.1).
  onSyncCardPayment?: (cardPayment: boolean) => Promise<boolean>;
}

const STEP_DONE_TEXT: Record<StepKey, string> = {
  post_order: 'Заказ-наряд проведён',
  print_service: 'Чек услуг пробит',
  print_goods: 'Чек запчастей пробит',
  post_sale: 'Реализация запчастей проведена',
  print_order: 'Наряд-заказ отправлен на принтер',
  post_service_sale: 'Реализация услуг проведена',
  create_service_invoice: 'Счёт-фактура услуг создана',
  print_service_invoice: 'Счёт-фактура услуг отправлена на принтер',
  create_goods_invoice: 'Счёт-фактура запчастей создана',
  print_goods_invoice: 'Счёт-фактура запчастей отправлена на принтер',
};

const STEP_MANUAL_LABEL: Record<StepKey, string> = {
  post_order: 'Провести',
  print_service: 'Пробить',
  print_goods: 'Пробить',
  post_sale: 'Провести',
  print_order: 'Печать',
  post_service_sale: 'Провести',
  create_service_invoice: 'Создать',
  print_service_invoice: 'Печать',
  create_goods_invoice: 'Создать',
  print_goods_invoice: 'Печать',
};

const PostAndPayModal: React.FC<Props> = ({ open, order, canPost, canFiscalize, canPrint, canInvoice, onClose, onSyncCardPayment }) => {
  const [ord, setOrd] = useState<ServiceOrder>(order);
  const [payment, setPayment] = useState<'cash' | 'card'>('cash');
  const [checks, setChecks] = useState<Record<StepKey, boolean>>({
    post_order: false, print_service: false, print_goods: false, post_sale: false, print_order: false,
    post_service_sale: false, create_service_invoice: false, print_service_invoice: false,
    create_goods_invoice: false, print_goods_invoice: false,
  });
  const [steps, setSteps] = useState<Partial<Record<StepKey, StepState>>>({});
  const [salesReceiptStep, setSalesReceiptStep] = useState<StepState>({ status: 'idle' });
  const [busy, setBusy] = useState<BusyKey | null>(null);
  const [changed, setChanged] = useState(false);
  const busyRef = useRef<BusyKey | null>(null);
  const initializedOrderRef = useRef<number | null>(null);

  const beginBusy = (key: BusyKey): boolean => {
    if (busyRef.current) return false;
    busyRef.current = key;
    setBusy(key);
    return true;
  };
  const endBusy = () => {
    busyRef.current = null;
    setBusy(null);
  };

  const hasServices = useMemo(
    () => ord.items.some((i) => i.item_type === 'service') || ord.total_services > 0,
    [ord],
  );
  const hasParts = useMemo(
    () => ord.items.some((i) => i.item_type === 'part') || ord.total_parts > 0,
    [ord],
  );
  const isLegal = ord.client_type === 'counterparty';
  const serviceSaleFlow = isLegal && (ord.payment_type === 'Банк' || !!ord.external_1c_service_sale_id);

  const postOrderDone = ord.posted && ord.status === 'Выполнено';
  const postOrderVisible = hasServices && !!ord.external_1c_id;
  const postOrderEnabled = postOrderVisible && !postOrderDone && canPost;

  const printServiceVisible = hasServices && (!isLegal || !serviceSaleFlow);
  const printServiceDone = ord.service_receipt_posted === true;
  const printServiceEnabled = printServiceVisible && !printServiceDone && canFiscalize;
  const printServiceManualEnabled = printServiceEnabled && !!ord.external_1c_service_receipt_id;

  const printGoodsVisible = hasParts && !isLegal && !!ord.external_1c_cash_receipt_id;
  const printGoodsDone = ord.receipt_posted === true;
  const printGoodsEnabled = printGoodsVisible && !printGoodsDone && canFiscalize;

  const postSaleVisible = hasParts && isLegal && !!ord.external_1c_sale_id;
  const postSaleDone = ord.sale_posted === true;
  const postSaleEnabled = postSaleVisible && !postSaleDone && canPost;

  const postServiceSaleVisible = hasServices && serviceSaleFlow;
  const postServiceSaleDone = ord.service_sale_posted === true;
  const postServiceSaleEnabled = postServiceSaleVisible && !postServiceSaleDone && canPost;

  const createServiceInvoiceVisible = hasServices && serviceSaleFlow;
  const createServiceInvoiceDone = ord.service_invoice_posted === true || !!ord.external_1c_service_invoice_id;
  const createServiceInvoiceEnabled = createServiceInvoiceVisible && !createServiceInvoiceDone && canInvoice;
  const printServiceInvoiceVisible = hasServices && serviceSaleFlow;
  const printServiceInvoiceEnabled = printServiceInvoiceVisible && canPrint;

  const createGoodsInvoiceVisible = hasParts && isLegal;
  const createGoodsInvoiceDone = ord.invoice_posted === true || !!ord.external_1c_invoice_id;
  const createGoodsInvoiceEnabled = createGoodsInvoiceVisible && !createGoodsInvoiceDone && canInvoice;
  const printGoodsInvoiceVisible = hasParts && isLegal;
  const printGoodsInvoiceEnabled = printGoodsInvoiceVisible && canPrint;

  const printOrderVisible = !!ord.external_1c_id;
  const printOrderEnabled = printOrderVisible && canPrint;

  const enabled: Record<StepKey, boolean> = {
    post_order: postOrderEnabled,
    print_service: printServiceEnabled,
    print_goods: printGoodsEnabled,
    post_sale: postSaleEnabled,
    print_order: printOrderEnabled,
    post_service_sale: postServiceSaleEnabled,
    create_service_invoice: createServiceInvoiceEnabled,
    print_service_invoice: printServiceInvoiceEnabled,
    create_goods_invoice: createGoodsInvoiceEnabled,
    print_goods_invoice: printGoodsInvoiceEnabled,
  };

  // Обновление order во время сохранения не должно сбрасывать выполняющуюся цепочку.
  useEffect(() => {
    if (!open) {
      initializedOrderRef.current = null;
      return;
    }
    if (initializedOrderRef.current === order.id) return;
    initializedOrderRef.current = order.id;
    const hs = order.items.some((i) => i.item_type === 'service') || order.total_services > 0;
    const hp = order.items.some((i) => i.item_type === 'part') || order.total_parts > 0;
    const legal = order.client_type === 'counterparty';
    const serviceSale = legal && (order.payment_type === 'Банк' || !!order.external_1c_service_sale_id);
    setOrd(order);
    setPayment(order.card_payment ? 'card' : 'cash');
    setChecks({
      post_order: hs && !!order.external_1c_id && !(order.posted && order.status === 'Выполнено') && canPost,
      print_service: hs && !serviceSale && order.service_receipt_posted !== true && canFiscalize,
      print_goods: hp && !legal && !!order.external_1c_cash_receipt_id && order.receipt_posted !== true && canFiscalize,
      post_sale: hp && legal && !!order.external_1c_sale_id && order.sale_posted !== true && canPost,
      print_order: !!order.external_1c_id && canPrint,
      post_service_sale: hs && serviceSale && order.service_sale_posted !== true && canPost,
      create_service_invoice: hs && serviceSale && !order.external_1c_service_invoice_id && canInvoice,
      print_service_invoice: hs && serviceSale && canPrint,
      create_goods_invoice: hp && legal && !order.external_1c_invoice_id && canInvoice,
      print_goods_invoice: hp && legal && canPrint,
    });
    setSteps({});
    setSalesReceiptStep({ status: 'idle' });
    busyRef.current = null;
    setBusy(null);
    setChanged(false);
  }, [open, order, canPost, canFiscalize, canPrint, canInvoice]);

  const setStep = (step: StepKey, state: StepState) =>
    setSteps((prev) => ({ ...prev, [step]: state }));

  // Перед командами сохраняем весь актуальный черновик, включая выбранный способ
  // оплаты; после сохранения цепочка использует только свежий ответ backend.
  const ensurePaymentSynced = async (): Promise<ServiceOrder | null> => {
    if (!onSyncCardPayment) return ord;
    const ok = await onSyncCardPayment(payment === 'card');
    if (!ok) return null;
    setChanged(true);
    try {
      const r = await api.get(`/service-orders/${ord.id}`);
      setOrd(r.data);
      return r.data as ServiceOrder;
    } catch {
      return null;
    }
  };

  const commandForStep = (step: StepKey, cur: ServiceOrder): ChainCommandInput => {
    if (step === 'post_order') {
      return {
        key: step,
        type: 'post_service_order',
        params: {
          require_service_receipt: checks.print_service && hasServices && !serviceSaleFlow && cur.service_receipt_posted !== true,
          ...(serviceSaleFlow ? {
            require_service_sale: checks.post_service_sale || checks.create_service_invoice || checks.print_service_invoice,
          } : {}),
        },
      };
    }
    if (step === 'post_sale') return { key: step, type: 'post_sale' };
    if (step === 'post_service_sale') return { key: step, type: 'post_sale', params: { document_role: 'services' } };
    if (step === 'create_service_invoice') return { key: step, type: 'create_tax_invoice', params: { document_role: 'services' } };
    if (step === 'create_goods_invoice') return { key: step, type: 'create_tax_invoice', params: { document_role: 'goods' } };
    if (step === 'print_service_invoice') return { key: step, type: 'print_tax_invoice', params: { document_role: 'services' } };
    if (step === 'print_goods_invoice') return { key: step, type: 'print_tax_invoice', params: { document_role: 'goods' } };
    if (step === 'print_order') return { key: step, type: 'print_service_order' };
    return {
      key: step,
      type: 'fiscalize_receipt',
      params: { payment_type: payment, receipt_kind: step === 'print_service' ? 'services' : 'goods' },
    };
  };

  const execStep = async (step: StepKey, cur: ServiceOrder): Promise<{ outcome: CommandOutcome; next: ServiceOrder }> => {
    const command = commandForStep(step, cur);
    const outcome = await runPostingCommand(command.type, cur.id, command.params);
    let next = cur;
    if (outcome.status === 'done') {
      setChanged(true);
      if (step === 'post_order' || step === 'post_sale' || step === 'post_service_sale'
        || step === 'create_service_invoice' || step === 'create_goods_invoice') {
        next = { ...cur, ...outcome.orderPatch };
        setOrd(next);
        const ready = step === 'post_order'
          ? next.posted === true && next.status === 'Выполнено'
            && (command.params?.require_service_receipt !== true || !!next.external_1c_service_receipt_id)
            && (command.params?.require_service_sale !== true || !!next.external_1c_service_sale_id)
          : step === 'post_sale' ? next.sale_posted === true
            : step === 'post_service_sale' ? next.service_sale_posted === true
              : step === 'create_service_invoice' ? next.service_invoice_posted === true && !!next.external_1c_service_invoice_id
                : next.invoice_posted === true && !!next.external_1c_invoice_id;
        if (!ready) {
          return {
            outcome: {
              status: 'error',
              error: 'Backend не вернул подтверждённые данные заказа. Проверьте документ в 1С; не запускайте операцию повторно вслепую.',
            },
            next,
          };
        }
      } else {
        try {
          const r = await api.get(`/service-orders/${cur.id}`);
          next = r.data;
          setOrd(r.data);
        } catch { /* оставляем прежний заказ */ }
      }
    }
    return { outcome, next };
  };

  const waitQueuedStep = async (
    step: StepKey,
    cmdId: number,
    cur: ServiceOrder,
  ): Promise<{ outcome: CommandOutcome; next: ServiceOrder }> => {
    const outcome = await waitPostingCommand(cmdId);
    let next = cur;
    if (outcome.status !== 'done') return { outcome, next };

    setChanged(true);
    if (step !== 'print_order') {
      next = { ...cur, ...outcome.orderPatch };
      setOrd(next);
    }
    const ready = step === 'post_sale' ? next.sale_posted === true
      : step === 'post_service_sale' ? next.service_sale_posted === true
        : step === 'create_service_invoice' ? next.service_invoice_posted === true && !!next.external_1c_service_invoice_id
          : step === 'create_goods_invoice' ? next.invoice_posted === true && !!next.external_1c_invoice_id
            : step === 'print_service' ? next.service_receipt_posted === true
              : step === 'print_goods' ? next.receipt_posted === true
                : true;
    if (!ready) {
      return {
        outcome: {
          status: 'error',
          error: 'Backend не вернул подтверждённый статус чека. Проверьте документ в 1С; не запускайте операцию повторно вслепую.',
        },
        next,
      };
    }
    return { outcome, next };
  };

  const applyOutcome = (step: StepKey, outcome: CommandOutcome) => {
    if (outcome.status === 'done') setStep(step, { status: 'done', text: outcome.message || STEP_DONE_TEXT[step] });
    else if (outcome.status === 'error') setStep(step, { status: 'error', text: outcome.error });
    else setStep(step, { status: 'timeout', text: outcome.error || '1С не ответила — проверьте агента на ПК кассира' });
  };

  const runOne = async (step: StepKey) => {
    if (!beginBusy(step)) return;
    if (step === 'print_order') {
      setStep(step, { status: 'running' });
      const ok = await printServiceOrderForm(ord.id);
      setStep(step, ok
        ? { status: 'done', text: STEP_DONE_TEXT.print_order }
        : { status: 'error', text: 'Не удалось отправить наряд-заказ на печать' });
      endBusy();
      return;
    }
    let cur = ord;
    if (step === 'print_service' || step === 'print_goods') {
      const synced = await ensurePaymentSynced();
      if (!synced) {
        message.error('Не удалось сохранить и перечитать заказ — пробитие не запущено');
        endBusy();
        return;
      }
      cur = synced;
    }
    setStep(step, { status: 'running' });
    const { outcome } = await execStep(step, cur);
    applyOutcome(step, outcome);
    endBusy();
  };

  // Проведение ЗН сначала создаёт чек услуг. Остальные команды затем ставятся
  // заранее, но исполняются агентом последовательно (OilChangePage.md №75).
  const runAll = async () => {
    if (!beginBusy('all')) return;
    let cur = ord;
    const synced = await ensurePaymentSynced();
    if (!synced) {
      message.error('Не удалось сохранить и перечитать заказ — цепочка не запущена');
      endBusy();
      return;
    }
    cur = synced;

    const hs = cur.items.some((i) => i.item_type === 'service') || cur.total_services > 0;
    const hp = cur.items.some((i) => i.item_type === 'part') || cur.total_parts > 0;
    const legal = cur.client_type === 'counterparty';
    const serviceSale = legal && (cur.payment_type === 'Банк' || !!cur.external_1c_service_sale_id);
    const seq: StepKey[] = [];
    if (checks.post_order && hs && !!cur.external_1c_id && !(cur.posted && cur.status === 'Выполнено') && canPost) seq.push('post_order');
    if (checks.print_service && hs && !serviceSale && cur.service_receipt_posted !== true && canFiscalize) seq.push('print_service');
    if (checks.post_service_sale && hs && serviceSale && cur.service_sale_posted !== true && canPost) seq.push('post_service_sale');
    if (checks.create_service_invoice && hs && serviceSale && !cur.external_1c_service_invoice_id && canInvoice) seq.push('create_service_invoice');
    if (checks.print_service_invoice && hs && serviceSale && canPrint) seq.push('print_service_invoice');
    if (checks.print_goods && hp && !legal && !!cur.external_1c_cash_receipt_id && cur.receipt_posted !== true && canFiscalize) seq.push('print_goods');
    if (checks.post_sale && hp && legal && !!cur.external_1c_sale_id && cur.sale_posted !== true && canPost) seq.push('post_sale');
    if (checks.create_goods_invoice && hp && legal && !cur.external_1c_invoice_id && canInvoice) seq.push('create_goods_invoice');
    if (checks.print_goods_invoice && hp && legal && canPrint) seq.push('print_goods_invoice');
    const wantPrintOrder = checks.print_order && !!cur.external_1c_id && canPrint;

    let broken = false;
    let failText = '';
    let postConfirmed = false;
    if (seq[0] === 'post_order') {
      setStep('post_order', { status: 'running' });
      const { outcome, next } = await execStep('post_order', cur);
      cur = next;
      applyOutcome('post_order', outcome);
      postConfirmed = outcome.status === 'done';
      if (outcome.status !== 'done') {
        broken = true;
        failText = outcome.error || '1С не ответила — проверьте агента на ПК кассира';
      }
      seq.shift();
    }

    if (!broken && seq.includes('print_service') && !cur.external_1c_service_receipt_id) {
      setStep('print_service', { status: 'error', text: 'Подтверждённый чек услуг не найден в ответе backend' });
      broken = true;
      failText = 'Печать чека услуг: подтверждённый чек не найден в ответе backend';
    }

    const queued = broken ? [] : [...seq];
    if (!broken && wantPrintOrder) queued.push('print_order');
    const chainId = `${cur.id}-${Date.now()}-${Math.random().toString(16).slice(2)}`;
    const { ids, errors }: { ids: Record<string, number>; errors: Record<string, string> } = legal
      ? { ids: {}, errors: {} }
      : await createPostingChain(
        cur.id,
        chainId,
        queued.map((step) => commandForStep(step, cur)),
      );

    for (const step of queued) {
      if (errors[step]) {
        setStep(step, { status: 'error', text: errors[step] });
        if (step !== 'print_order') {
          broken = true;
          failText = errors[step];
        }
        break;
      }
      const cmdId = ids[step];
      if (!legal && !cmdId) break;
      setStep(step, { status: 'running' });
      const { outcome, next } = legal
        ? await execStep(step, cur)
        : await waitQueuedStep(step, cmdId, cur);
      cur = next;
      applyOutcome(step, outcome);
      if (outcome.status !== 'done') {
        broken = true;
        failText = outcome.error || '1С не ответила — проверьте агента на ПК кассира';
        break;
      }
    }

    if (!broken && Object.keys(ids).length > 0) {
      try {
        const r = await api.get(`/service-orders/${cur.id}`);
        setOrd({
          ...r.data,
          ...(postConfirmed ? { posted: cur.posted, status: cur.status } : {}),
          ...(ids.print_service ? { service_receipt_posted: cur.service_receipt_posted } : {}),
          ...(ids.print_goods ? { receipt_posted: cur.receipt_posted } : {}),
          ...(ids.post_sale ? { sale_posted: cur.sale_posted } : {}),
        });
      } catch { /* подтверждённые order_patch уже применены */ }
    }
    if (broken && failText) message.error(`Цепочка прервана. ${failText}`);
    endBusy();
  };

  const runSalesReceipt = async () => {
    if (!beginBusy('sales_receipt')) return;
    setSalesReceiptStep({ status: 'running' });
    const ok = await printSalesReceiptForm(ord.id);
    setSalesReceiptStep(ok
      ? { status: 'done', text: 'Товарный чек отправлен на принтер' }
      : { status: 'error', text: 'Не удалось отправить товарный чек на печать' });
    endBusy();
  };

  const anyCheckedEnabled = (Object.keys(checks) as StepKey[]).some((k) => checks[k] && enabled[k]);

  const renderStepState = (st?: StepState) => {
    if (!st || st.status === 'idle') return null;
    if (st.status === 'running') return <Spin size="small" />;
    if (st.status === 'done') return <Text type="success" style={{ fontSize: 12 }}><CheckCircleOutlined /> {st.text}</Text>;
    if (st.status === 'error') return <Text type="danger" style={{ fontSize: 12 }}><CloseCircleOutlined /> {st.text}</Text>;
    return <Text type="warning" style={{ fontSize: 12 }}>{st.text}</Text>;
  };

  const renderStatus = (step: StepKey) => renderStepState(steps[step]);

  const renderStep = (
    step: StepKey,
    label: string,
    opts: { enabled: boolean; doneText?: string; manualDisabled?: boolean; hint?: string },
  ) => (
    <div key={step} style={{ marginBottom: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <Checkbox
          checked={opts.enabled ? checks[step] : false}
          disabled={!opts.enabled || !!busy}
          onChange={(e) => setChecks((prev) => ({ ...prev, [step]: e.target.checked }))}
        >
          {label}
          {!opts.enabled && opts.doneText && <Text type="secondary" style={{ fontSize: 12 }}> — {opts.doneText}</Text>}
        </Checkbox>
        <span style={{ flex: 1 }} />
        {renderStatus(step)}
        <Button
          size="small"
          disabled={!opts.enabled || opts.manualDisabled || !!busy}
          loading={busy === step}
          onClick={() => runOne(step)}
        >
          {STEP_MANUAL_LABEL[step]}
        </Button>
      </div>
      {opts.hint && opts.enabled && (
        <div style={{ marginLeft: 24 }}>
          <Text type="secondary" style={{ fontSize: 12 }}>{opts.hint}</Text>
        </div>
      )}
    </div>
  );

  return (
    <Modal
      open={open}
      title={`Провести и оплатить — ЗН № ${order.number || ''}`}
      onCancel={() => !busy && onClose(changed)}
      footer={null}
      width={520}
    >
      {(!isLegal || printServiceVisible) && (
        <div style={{ marginBottom: 16 }}>
          <Text strong style={{ marginRight: 12 }}>Оплата:</Text>
          <Radio.Group
            value={payment}
            onChange={(e) => setPayment(e.target.value)}
            disabled={!!busy}
          >
            <Radio value="cash">Наличные</Radio>
            <Radio value="card">Карта</Radio>
          </Radio.Group>
        </div>
      )}

      {hasServices && (
        <div style={{ marginBottom: 16 }}>
          <Text strong>Услуги (ИП Фомин Д.А.)</Text>
          <div style={{ marginTop: 8 }}>
            {postOrderVisible && renderStep('post_order', 'Проведение заказ-наряда', {
              enabled: postOrderEnabled,
              doneText: postOrderDone ? 'уже проведён' : (!canPost ? 'отключено администратором' : undefined),
            })}
            {postServiceSaleVisible && renderStep('post_service_sale', 'Проведение реализации услуг', {
              enabled: postServiceSaleEnabled,
              doneText: postServiceSaleDone ? 'уже проведена' : (!canPost ? 'отключено администратором' : undefined),
              manualDisabled: !ord.external_1c_service_sale_id,
              hint: !ord.external_1c_service_sale_id ? 'Реализация будет найдена после проведения заказ-наряда' : undefined,
            })}
            {createServiceInvoiceVisible && renderStep('create_service_invoice', 'Создание счёта-фактуры услуг', {
              enabled: createServiceInvoiceEnabled,
              doneText: createServiceInvoiceDone ? `уже создана${ord.external_1c_service_invoice_number ? ` № ${ord.external_1c_service_invoice_number}` : ''}` : (!canInvoice ? 'отключено администратором' : undefined),
              manualDisabled: !ord.external_1c_service_sale_id,
            })}
            {printServiceInvoiceVisible && renderStep('print_service_invoice', 'Печать счёта-фактуры услуг', {
              enabled: printServiceInvoiceEnabled,
              doneText: !canPrint ? 'отключено администратором' : undefined,
              manualDisabled: !ord.external_1c_service_invoice_id,
            })}
            {printServiceVisible && renderStep('print_service', 'Печать чека услуг', {
              enabled: printServiceEnabled,
              doneText: printServiceDone ? 'уже пробит' : (!canFiscalize ? 'отключено администратором' : undefined),
              manualDisabled: !printServiceManualEnabled,
              hint: !ord.external_1c_service_receipt_id ? 'Чек будет создан автоматически при проведении заказ-наряда' : undefined,
            })}
            {printOrderVisible && renderStep('print_order', 'Печать заказ-наряда', {
              enabled: printOrderEnabled,
              doneText: !canPrint ? 'отключено администратором' : undefined,
            })}
          </div>
        </div>
      )}

      {hasParts && (
        <div style={{ marginBottom: 16 }}>
          <Text strong>Запчасти (АвтоРегиум)</Text>
          <div style={{ marginTop: 8 }}>
            {printGoodsVisible && renderStep('print_goods', 'Печать чека запчастей', {
              enabled: printGoodsEnabled,
              doneText: printGoodsDone ? 'уже пробит' : (!canFiscalize ? 'отключено администратором' : undefined),
            })}
            {postSaleVisible && renderStep('post_sale', 'Проведение реализации запчастей', {
              enabled: postSaleEnabled,
              doneText: postSaleDone ? 'уже проведена' : (!canPost ? 'отключено администратором' : undefined),
            })}
            {createGoodsInvoiceVisible && renderStep('create_goods_invoice', 'Создание счёта-фактуры запчастей', {
              enabled: createGoodsInvoiceEnabled,
              doneText: createGoodsInvoiceDone ? `уже создана${ord.external_1c_invoice_number ? ` № ${ord.external_1c_invoice_number}` : ''}` : (!canInvoice ? 'отключено администратором' : undefined),
              manualDisabled: !ord.external_1c_sale_id,
            })}
            {printGoodsInvoiceVisible && renderStep('print_goods_invoice', 'Печать счёта-фактуры запчастей', {
              enabled: printGoodsInvoiceEnabled,
              doneText: !canPrint ? 'отключено администратором' : undefined,
              manualDisabled: !ord.external_1c_invoice_id,
            })}
          </div>
        </div>
      )}

      {printOrderVisible && (
        <div style={{ marginBottom: 16 }} data-testid="sales-receipt-print-row">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Text>Печать товарного чека</Text>
            <span style={{ flex: 1 }} />
            {renderStepState(salesReceiptStep)}
            <Button
              size="small"
              disabled={!canPrint || !!busy}
              loading={busy === 'sales_receipt'}
              onClick={runSalesReceipt}
            >
              Печать
            </Button>
          </div>
          {!canPrint && (
            <Text type="secondary" style={{ fontSize: 12 }}>Печать через 1С отключена администратором</Text>
          )}
        </div>
      )}

      {!hasServices && !hasParts && (
        <Text type="secondary">В заказе нет ни услуг, ни запчастей.</Text>
      )}

      <Button
        type="primary"
        block
        size="large"
        disabled={!anyCheckedEnabled || !!busy}
        loading={busy === 'all'}
        onClick={runAll}
      >
        Провести и оплатить
      </Button>
    </Modal>
  );
};

export default PostAndPayModal;
